import React, { ChangeEvent, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { useAssets, useLocale } from 'hooks';
import { useFormContext } from 'react-hook-form';

import { StepContainer } from '../stepContainer/StepContainer';
import { GenericField, ScanField } from 'form/fields';
import { Button, Autocomplete } from 'ui';
import { uuid } from 'form/validators/uuid/uuid';

import { StepFormDetailsManufacturerProps } from './StepFormDetailsManufacturer.types';
import { useStyles } from './StepFormDetailsManufacturer.styles';

export const StepFormDetailsManufacturer = ({ goToNextStep, goToPreviousStep }: StepFormDetailsManufacturerProps) => {
  const { formatMessage } = useLocale();
  const classes = useStyles();
  const { setValue, trigger, formState } = useFormContext();
  const { getPredefinedBatteriesOptions, getPredefinedBatteryByModelName } = useAssets();

  useEffect(() => {
    trigger('id');
  }, []);

  const handleChange = (e: ChangeEvent<Record<string, string>>, value: string | null) => {
    if (value) {
      const battery = getPredefinedBatteryByModelName(value);
      if (!battery) return;
      setValue('chemicalType', battery.chemicalType);
      setValue('capacity', battery.capacity);
      setValue('weight', battery.weight);
      setValue('power', battery.power);
      setValue('maximumVoltage', battery.maximumVoltage);
      setValue('cost', battery.cost || 1.23);
    }
  };

  return (
    <StepContainer title={formatMessage({ id: `issue_battery.form.details_title` })}>
      <Grid container className={classes.container} spacing={1}>
        <Grid item xs={12}>
          <ScanField
            onScan={value => setValue('id', value, { shouldValidate: true, shouldDirty: true })}
            label={formatMessage({ id: 'issue_battery.form.id' })}
            name="id"
            required
            validation={{
              validate: uuid,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <ScanField
            onScan={value => setValue('serialNumber', value, { shouldValidate: true, shouldDirty: true })}
            label={formatMessage({ id: 'issue_battery.form.serial_number' })}
            name="serialNumber"
            required
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            options={getPredefinedBatteriesOptions()}
            onChange={handleChange}
            freeSolo
            renderInput={params => (
              <GenericField label={formatMessage({ id: 'issue_battery.form.model' })} name="model" {...params} />
            )}
            data-testid="autocomplete"
          />
        </Grid>
        <Grid item xs={12}>
          <GenericField label={formatMessage({ id: 'issue_battery.form.chemical_type' })} name="chemicalType" />
        </Grid>
        <Grid item xs={6}>
          <GenericField label={formatMessage({ id: 'issue_battery.form.capacity' })} name="capacity" />
        </Grid>
        <Grid item xs={6}>
          <GenericField label={formatMessage({ id: 'issue_battery.form.power' })} name="power" />
        </Grid>
        <Grid item xs={6}>
          <GenericField label={formatMessage({ id: 'issue_battery.form.maximumVoltage' })} name="maximumVoltage" />
        </Grid>
        <Grid item xs={6}>
          <GenericField label={formatMessage({ id: 'issue_battery.form.weight' })} name="weight" />
        </Grid>
        <Grid container className={classes.actions} spacing={2}>
          <Grid item xs={6}>
            <Button variant="outlined" color="primary" onClick={goToPreviousStep}>
              {formatMessage({ id: 'common.back' })}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="primary"
              onClick={formState.isValid ? goToNextStep : undefined}
              disabled={!formState.isValid}
            >
              {formatMessage({ id: 'common.continue' })}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </StepContainer>
  );
};
